'use client';
import React from 'react';

import styled from '@benzinga/themetron';
import { Dropdown, ErrorBoundary } from '@benzinga/core-ui';
import EditorialPushNotificationModal from './EditorialPushNotificationModal';
import { ContentManager } from '@benzinga/content-manager';
import { SessionContext } from '@benzinga/session-context';

interface EditorBoxProps {
  nid: number;
  postId: number;
  type: string;
  title: string;
  canonicalUrl: string;
  useNewTemplate?: boolean;
}

export const EditorBox: React.FC<EditorBoxProps> = ({ canonicalUrl, nid, postId, title, type, useNewTemplate }) => {
  const [isEditorialPushNotificationModalOpen, setIsEditorialPushNotificationModalOpen] = React.useState(false);

  const session = React.useContext(SessionContext);

  const handleClearCache = React.useCallback(async () => {
    const contentManager = session.getManager(ContentManager);
    const clearCacheResponse = await contentManager.clearNodeCache(nid);
    const clearCacheData = clearCacheResponse.ok;
    console.log('clear cache' + clearCacheData); // Debug purpose only for temporary
    global.location.reload();
    // contentManager.clearNodeCache(nid).then(() => {
    //   global.location.reload();
    // });
  }, [nid, session]);

  const handleOpenEditorialPushNotificationModal = () => {
    setIsEditorialPushNotificationModalOpen(true);
  };

  const handleCloseEditorialPushNotificationModal = () => {
    setIsEditorialPushNotificationModalOpen(false);
  };

  return (
    <Container className="article-editor-box flex flex-col">
      <EditorToolbar>
        <ul className="tabs primary">
          <li>
            <a href={`https://www.benzinga.com/node/${nid}/edit`}>
              <span>Edit</span>
            </a>
          </li>
          {postId && type === 'benzinga_contributor' ? (
            <li>
              <a href={`https://contributor.benzinga.com/wp-admin/post.php?post=${postId}&action=edit`}>
                <span>Edit Contributor CMS</span>
              </a>
            </li>
          ) : (
            postId && (
              <li>
                <a href={`https://editorial.benzinga.com/wp-admin/post.php?post=${postId}&action=edit`}>
                  <span>Edit Editorial CMS</span>
                </a>
              </li>
            )
          )}
          <li>
            <a href={`https://www.benzinga.com/node/add/story?nid=${nid}`} rel="noreferrer" target="_blank">
              Republish
            </a>
          </li>
          <li>
            <a href={`https://www.benzinga.com/node/${nid}/revisions`}>
              <span>Revisions</span>
            </a>
          </li>
          <li>
            <a href={`https://www.benzinga.com/node/${nid}/workflow`}>
              <span>Workflow</span>
            </a>
          </li>
          <li>
            <a href={`https://www.benzinga.com/node/${nid}/nodequeue`}>
              <span>Nodequeue</span>
            </a>
          </li>
          <li>
            <Dropdown
              className={`${useNewTemplate ? 'bg-[#283D59]' : 'bg-white'} rounded shadow-lg overflow-hidden`}
              closeWhenClickOutside={false}
              distance={0}
              fluid
              headless={true}
              target={<span className="cursor-pointer">Translate</span>}
            >
              <ul>
                <li>
                  <a
                    href={`https://admin-tools.benzinga.com/api/node/${nid}/translate?website=1`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Italian
                  </a>
                </li>
                <li>
                  <a
                    href={`https://admin-tools.benzinga.com/api/node/${nid}/translate?website=4`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    French
                  </a>
                </li>
                <li>
                  <a
                    href={`https://admin-tools.benzinga.com/api/node/${nid}/translate?website=2`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Spanish
                  </a>
                </li>
              </ul>
            </Dropdown>
          </li>
          <li>
            <Dropdown
              className={`${useNewTemplate ? 'bg-[#283D59]' : 'bg-white'} rounded shadow-lg overflow-hidden`}
              closeWhenClickOutside={false}
              distance={0}
              fluid
              headless={true}
              target={<span className="cursor-pointer">Translate v2</span>}
            >
              <ul>
                <li>
                  <a
                    href={`https://editorial.benzinga.com/wp-admin/admin.php?page=bz_translation_nid&original_nid=${nid}&selectsite=benzinga_it`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Italian
                  </a>
                </li>
                <li>
                  <a
                    href={`https://editorial.benzinga.com/wp-admin/admin.php?page=bz_translation_nid&original_nid=${nid}&selectsite=benzinga_fr`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    French
                  </a>
                </li>
                <li>
                  <a
                    href={`https://editorial.benzinga.com/wp-admin/admin.php?page=bz_translation_nid&original_nid=${nid}&selectsite=benzinga_es`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Spanish
                  </a>
                </li>
                <li>
                  <a
                    href={`https://editorial.benzinga.com/wp-admin/admin.php?page=bz_translation_nid&original_nid=${nid}&selectsite=benzinga_kr`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Korean
                  </a>
                </li>
                <li>
                  <a
                    href={`https://editorial.benzinga.com/wp-admin/admin.php?page=bz_translation_nid&original_nid=${nid}&selectsite=benzinga_jp`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Japanese
                  </a>
                </li>
              </ul>
            </Dropdown>
          </li>
          <li className="cursor-pointer text-red-500">
            <span onClick={handleClearCache}>Clear Cache</span>
          </li>
          <li>
            <a href={`https://tools.zing.benzinga.com/nova/resources/nodes/${nid}`}>
              <span>Alert Tools</span>
            </a>
          </li>
          <li onClick={handleOpenEditorialPushNotificationModal}>
            <span className="cursor-pointer">Send Push</span>
          </li>
          <ErrorBoundary name="editorial-push-notification-modal">
            <EditorialPushNotificationModal
              isOpen={isEditorialPushNotificationModalOpen}
              nid={nid}
              onClose={handleCloseEditorialPushNotificationModal}
              title={title}
              url={canonicalUrl}
            />
          </ErrorBoundary>
        </ul>
      </EditorToolbar>
    </Container>
  );
};

const Container = styled.div`
  margin: 0 auto;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const EditorToolbar = styled.div`
  //margin: 12px 0;
  overflow-x: auto;
  white-space: nowrap;
  display: flex;
  ul {
    display: inline-flex;
    border: solid 1px ${({ theme }) => theme.colors.foreground};

    li {
      padding: 4px 12px;
      background: ${({ theme }) => theme.colors.backgroundActive};
      /* span {
        color: ${({ theme }) => theme.colors.foreground};
      } */
    }
  }
`;
